import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { db } from '@/data/supabase';

function PhotoSlot({ kind, label, photos, onCapture, busy }) {
  const fileRef = useRef(null);
  const mine = photos.filter(p => p.kind === kind);

  return (
    <div style={{
      border: '1px solid var(--b1)',
      borderRadius: 10,
      padding: 12,
      background: 'var(--s2)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
          {label} <span style={{ fontWeight: 400, color: 'var(--t3)', fontSize: 11 }}>({mine.length})</span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onCapture(file);
            e.target.value = '';
          }}
        />
        <button
          className="btn btn-primary btn-sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {busy ? '…' : '📷 Capture'}
        </button>
      </div>
      {mine.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--t3)', padding: '12px 4px' }}>
          No {label.toLowerCase()} photos yet
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6 }}>
          {mine.map(p => (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'block', position: 'relative', borderRadius: 6, overflow: 'hidden', background: 'var(--s3)' }}
            >
              <img
                src={p.url}
                alt={kind}
                style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StopPhotosModal({ data }) {
  const { stops, custById, uploadPhoto, closeModal, cloudMode } = useApp();
  const stop = data?.stopId ? stops.find(s => s.id === data.stopId) : null;
  const c = stop ? custById(stop.custId) : (data?.custId ? custById(data.custId) : null);

  const [photos, setPhotos]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);

  // Load photos for this stop/customer
  useEffect(() => {
    if (!cloudMode) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = stop
        ? await db.photos.listForStop(stop.id)
        : (c ? await db.photos.listForCustomer(c.id) : { data: [] });
      if (!cancelled) {
        setPhotos(res?.data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stop?.id, c?.id, cloudMode]);

  const handleCapture = async (kind, file) => {
    setUploading(true);
    const res = await uploadPhoto({
      file,
      stopId: stop?.id,
      customerId: c?.id,
      kind,
      tech: stop?.tech,
    });
    setUploading(false);
    if (res.ok && res.photo) {
      setPhotos(prev => [...prev, res.photo]);
    }
  };

  if (!c) return null;

  return (
    <div className="modal" style={{ width: 'min(560px, 95vw)' }}>
      <div className="modal-head">
        <span className="modal-title">Photos — {c.name}</span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <div className="modal-body">
        {loading && <div style={{ fontSize: 12, color: 'var(--t3)' }}>Loading…</div>}

        <div style={{ display: 'grid', gap: 10 }}>
          <PhotoSlot
            kind="before"
            label="Before"
            photos={photos}
            busy={uploading}
            onCapture={(file) => handleCapture('before', file)}
          />
          <PhotoSlot
            kind="after"
            label="After"
            photos={photos}
            busy={uploading}
            onCapture={(file) => handleCapture('after', file)}
          />
          <PhotoSlot
            kind="issue"
            label="Issue / Equipment"
            photos={photos}
            busy={uploading}
            onCapture={(file) => handleCapture('issue', file)}
          />
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--t3)' }}>
          Photos are watermarked with timestamp + tech name and stored in the cloud.
        </div>
      </div>
    </div>
  );
}
